import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  CONTACT_AVATAR_BUCKET,
  CONTACT_AVATAR_URL_TTL_SECONDS,
} from "@/lib/contactAvatar";

const CHUNK = 100;
const STALE_MS = (CONTACT_AVATAR_URL_TTL_SECONDS - 10 * 60) * 1000;

/** A signed link for one stored photo, or null. */
export function useContactAvatarUrl(path: string | null | undefined) {
  return useQuery({
    queryKey: ["contact-avatar-url", path],
    enabled: !!path,
    staleTime: STALE_MS,
    queryFn: async () => {
      const { data, error } = await supabase.storage
        .from(CONTACT_AVATAR_BUCKET)
        .createSignedUrl(path!, CONTACT_AVATAR_URL_TTL_SECONDS);
      if (error) return null;
      return data.signedUrl;
    },
  }).data ?? null;
}

/**
 * Photo links for many Customer Graph contacts at once (the Inbox list):
 * one lookup for the paths and one signing request. Best-effort: any error
 * just means initials are shown.
 */
export function useContactAvatarUrls(businessId: string | null | undefined, contactIds: string[]) {
  const ids = [...new Set(contactIds)].sort();
  return useQuery({
    queryKey: ["contact-avatar-urls", businessId, ids.join(",")],
    enabled: !!businessId && ids.length > 0,
    staleTime: STALE_MS,
    queryFn: async () => {
      const urls = new Map<string, string>();
      try {
        const pathByContact = new Map<string, string>();
        for (let index = 0; index < ids.length; index += CHUNK) {
          const { data, error } = await (supabase as any)
            .from("contacts")
            .select("contact_id, avatar_path")
            .eq("business_id", businessId)
            .in("contact_id", ids.slice(index, index + CHUNK))
            .not("avatar_path", "is", null);
          if (error) return urls;
          for (const row of data ?? []) pathByContact.set(row.contact_id, row.avatar_path);
        }
        const paths = [...new Set(pathByContact.values())];
        if (paths.length === 0) return urls;
        const { data: signed, error } = await supabase.storage
          .from(CONTACT_AVATAR_BUCKET)
          .createSignedUrls(paths, CONTACT_AVATAR_URL_TTL_SECONDS);
        if (error) return urls;
        const urlByPath = new Map(
          (signed ?? []).filter((item) => item.signedUrl).map((item) => [item.path, item.signedUrl]),
        );
        for (const [contactId, path] of pathByContact) {
          const url = urlByPath.get(path);
          if (url) urls.set(contactId, url);
        }
      } catch {
        // Photos are decoration; never break the Inbox over them.
      }
      return urls;
    },
  }).data ?? new Map<string, string>();
}
